import { Component, OnInit, HostListener, ChangeDetectionStrategy } from "@angular/core";
import { Patient } from "../model/patient.";
import { UtilityService } from "../shared/service/utility.service";
import { ConfirmationDialogComponent } from "../confirm.dialog/confirmation.dialog.component";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { EhrPatientService } from "../patient/ehr-patient.service";

import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { SpinnerService } from "../spinner/spinner.service";
import { AngularAppLogger } from "../angular-app-logger";
import { AppConfig}  from "../shared/constants/app.config";
import { MedicationsService } from "../medications/medications.service";
import {ObservationLabsService} from '../observation.labs/observation.labs.service';

@Component({
    selector: "app-patient.detail",
    templateUrl: "./patient.detail.component.html",
    styleUrls: ["./patient.detail.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PatientDetailComponent implements OnInit {
  ehrpatient: Patient;
  crid: string;
  activeLabel: string;
  priority: any;
  priorityState: any;
  dialogRef: MatDialogRef<ConfirmationDialogComponent>;
  labsState: any;
  medicationState: any;
  isR4: boolean;
  dateRangeInMonths: any;
  startDate: Date;
  endDate: Date;
  maxDate: Date;
  vitalState: any;
  patientName: string;
  isDateRangeApplied = false;


  constructor(
    readonly utility: UtilityService,
    readonly spinner: SpinnerService,
    readonly dialog: MatDialog,
    readonly ehrpatientService: EhrPatientService,

    readonly router: Router,
    readonly route: ActivatedRoute,
    readonly appLogger: AngularAppLogger,
    readonly medicationsService: MedicationsService,
    readonly observationLabsService: ObservationLabsService
  ) {
    const data = utility.data;
    this.ehrpatient = JSON.parse(data.ehrpatient);
    this.patientName = this.name(this.ehrpatient);
    this.crid = data.crid;
    this.maxDate = new Date();
  }

  ngOnInit() {
    this.isR4 = localStorage.getItem("fhir_version")?.includes("R4") || false;

    this.endDate ??= this.maxDate;
    this.startDate ??= new Date(this.endDate);
    this.startDate.setFullYear(this.startDate.getFullYear() - 2);
    localStorage.setItem("startDate", this.startDate.toISOString().slice(0, 10));
    localStorage.setItem("endDate", this.endDate.toISOString().slice(0, 10));
    this.dateRangeInMonths = this.utility.data.dateRangeInMonths ?? this.getMonthDifference(this.startDate, this.endDate);
    this.utility.data.dateRangeInMonths = this.dateRangeInMonths;
    this.utility.data.startDate = this.startDate;
    this.utility.data.endDate = this.endDate;
    this.isDateRangeApplied = true;
  }

  setSelectedDateRange(startDate, endDate) {
    this.endDate = endDate;
    this.startDate = startDate;
    this.dateRangeInMonths = this.getMonthDifference(this.startDate, this.endDate);
    this.utility.data.dateRangeInMonths = this.dateRangeInMonths;
    this.utility.data.startDate = this.startDate;
    this.utility.data.endDate = this.endDate;
    this.appLogger.logInfo(`Start date - ${this.startDate}` , { context: `Set Start Date`, allowDuplicate: true });
    this.appLogger.logInfo(`End date -  ${this.endDate}`, { context: `Set End Date`, allowDuplicate: true });
    localStorage.setItem("startDate", this.startDate.toISOString().slice(0, 10));
    localStorage.setItem("endDate", this.endDate.toISOString().slice(0, 10));
  }

  getMonthDifference(start: Date, end: Date): number {
    let months =
      (end.getUTCFullYear() - start.getUTCFullYear()) * 12 +
      (end.getUTCMonth() - start.getUTCMonth());

    const startDay = start.getUTCDate();
    const endDay = end.getUTCDate();

    if (endDay > startDay) {
      months++;
    }
    return months;
  }

  get showDateRange(): boolean {
    return ['priority', 'lab', 'vitalsigns'].includes(this.activeLabel);
  }

  onDateRangeApplied(range: { start: Date; end: Date }): void {
    this.setSelectedDateRange(range.start, range.end);
    this.isDateRangeApplied = true;
    this.refetchActiveCollection();
  }

  private refetchActiveCollection(): void {
    switch (this.activeLabel) {
      case 'priority':   this.fetchPriorityLabs(); break;
      case 'lab':        this.fetchAllLabs();       break;
      case 'vitalsigns': this.fetchVitals();        break;
    }
  }

  onDateRangeCleared(): void {
    const end = this.maxDate;
    const start = new Date(end);
    start.setFullYear(start.getFullYear() - 2);
    this.setSelectedDateRange(start, end);
    this.isDateRangeApplied = true;
  }

  //TODO : used the same logic multiple places
  name(ehrpatient) {
    const name = ehrpatient.name.filter(
      (e) => e.use === "official" || e.use === "usual"
    );
    if (!name || name.length === 0) {
      this.appLogger.logError("Patient name does not contain official or usual use tags. Name array in Patient - " + JSON.stringify(ehrpatient.name), {
        context: `Error with name of the patient in Patient detail component` });
      throw new Error("Error in Patient data. Name does not contain official or usual use info");
    }
    return name[0]?.text ? name[0].text : name[0].given[0] + " " + name[0].family;
  }

  @HostListener("click", ["$event"])
  onClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target.id !== "") {
      this.activeLabel = target.id;
    }
  }

  private fetchPriorityLabs(): void {
    this.spinner.start();
    this.ehrpatientService
      .getObservationPriorityLabs(this.ehrpatient.id)
      .subscribe({
        next: (data) => {
          if (data) {
            this.utility.data.priority = JSON.stringify(data);
            this.routePriorityLabs(data);
          }
          this.spinner.end();
          this.appLogger.logInfo('url info ', {url: this.utility.data.url});
        },
        error: (error) => {
          this.priorityState = "error";
          this.spinner.reset();
          this.router.navigate(["./error"], { relativeTo: this.route });
          this.appLogger.logError(error, {
            component: "PatientDetailComponent",
            context: "getPriorityLabs",
            url: this.utility.data.url,
            crid: this.crid,
            startDate: this.utility.data.startDate,
            endDate: this.utility.data.endDate
          });
        }
      });
  }

  getPriorityLabs() {
    this.dialogRef.afterClosed().subscribe((result) => {
      if (result) this.fetchPriorityLabs();
      this.dialogRef = null;
    });
  }

  routePriorityLabs(data) {
    this.priorityState = this.utility.bundleResources(JSON.stringify(data));
    this.observationLabsService.setPriorityLabsValue(true);
    if (this.priorityState.total === 0) {
      this.router.navigate(["./info"], {
        relativeTo: this.route,
      });
    } else {
      if (this.router.url === "/patientdetail/priority") {
        this.router.navigateByUrl("/patientdetail").then(() => {
          this.router.navigate(['./priority'], {
            relativeTo: this.route,
          });
        });
      } else {
        this.router.navigate(["./priority"], {
          relativeTo: this.route,
        });
      }
    }
  }

  private fetchAllLabs(): void {
    this.spinner.start();
    this.ehrpatientService
      .getObservationLabs(this.ehrpatient.id)
      .subscribe({
        next: (data) => {
          if (data) {
            this.utility.data.labs = JSON.stringify(data);
            this.routeLabs(data);
          }
          this.spinner.end();
        },
        error: (error) => {
          this.labsState = "error";
          this.spinner.reset();
          this.router.navigate(["./error"], { relativeTo: this.route });
          this.appLogger.logError(error, {
            component: "PatientDetailComponent",
            context: "getAllLabs",
            url: this.utility.data.url,
            crid: this.crid,
            startDate: this.utility.data.startDate,
            endDate: this.utility.data.endDate
          });
        }
      });
  }

  getAllLabs() {
    this.dialogRef.afterClosed().subscribe((result) => {
      if (result) this.fetchAllLabs();
      this.dialogRef = null;
    });
  }

  // Simplified the routing code for all labs
  routeLabs(data) {
    this.labsState = this.utility.bundleResources(JSON.stringify(data));
    this.observationLabsService.setPriorityLabsValue(false);
    const path = this.labsState.total === 0 ? './info' : './labs';

    if (this.router.url === '/patientdetail/labs' && path === './labs') {
      // Navigate to '/patientdetail' and then to './labs'
      this.router.navigateByUrl('/patientdetail').then(() => {
        this.router.navigate([path], { relativeTo: this.route });
      });
    } else {
      // Navigate directly to the 'path'
      this.router.navigate([path], { relativeTo: this.route });
    }
  }

  openMedsConfirmationDialog() {
    if (!this.utility.data.medicationrequest) {
      this.dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        width: '490px',
        panelClass: "custom-dialog-container",
        disableClose: false
      });
      this.dialogRef.componentInstance.confirmMessage =
        "Retrieval Medications may take several minutes. Do you want to continue?";
      this.dialogRef.componentInstance.showCheckbox = false;

      this.dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.spinner.start(); // Start spinner when API call begins
          this.ehrpatientService
            .getMedicationRequest(this.ehrpatient.id)
            .subscribe(
              (data) => {
                if (data) {
                  // Check and make sure only the resource types we want are being returned and OperationOutcome resources are ignored
                  const filteredMeds = data.filter(entry => {
                    return AppConfig.RESOURCE_TYPE.includes(entry.resource?.resourceType);
                  });
                  this.utility.data.medicationrequest = JSON.stringify(filteredMeds);
                  this.routeMedication(filteredMeds); // Route after data is fetched
                }
              },
              (error) => {
                this.medicationState = "error";
                this.spinner.reset(); // Reset spinner on error
                this.router.navigate(["./error"], { relativeTo: this.route });
                this.appLogger.logError(error, {
                  component: "PatientDetailComponent",
                  context: "openMedsConfirmationDialog",
                  url: this.utility.data.url,
                  crid: this.crid
                });
              }
            );
        }
        this.dialogRef = null;
      });
    } else {
      // Handle cached data scenario
      this.spinner.start(); // Start spinner before routing
      const data = JSON.parse(this.utility.data.medicationrequest);
      this.routeMedication(data);
    }
  }

  routeMedication(data) {
    const hasMedication = data.length > 0;
    const routePath = hasMedication ? "./medications" : "./info";

    this.medicationState = hasMedication
      ? this.utility.bundleResources(JSON.stringify(data))
      : this.utility.bundleResources(JSON.stringify([]));

    this.spinner.start(); // Start the spinner before navigation

    this.router.navigate([routePath], { relativeTo: this.route }).then(() => {
      const navigationSubscription = this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          //Do the following only for /medications route ( do not check /info anymore)
          // - just unsubscribe and return otherwise
          if (this.router.url.endsWith('/medications')) {
            this.medicationsService.medications$
              .subscribe(
                (medicationData) => {
                  if (!medicationData) {
                    this.medicationState = "error";
                    this.router.navigate(["./error"], {
                      relativeTo: this.route,
                      queryParams: { reason: "cancel" },
                    });
                  } else if (medicationData.length === 0) {
                    this.medicationState = this.utility.bundleResources(
                      JSON.stringify(medicationData)
                    );
                    this.router.navigate(["./info"], {
                      relativeTo: this.route,
                      queryParams: { reason: "empty" },
                    });
                  } else {
                    this.medicationState = this.utility.bundleResources(
                      JSON.stringify(medicationData)
                    );
                  }
                },
                (error) => {
                  this.appLogger.logError(error, { context: "Error in routing to Meds Resolver and Component"});
                }
              )
              .add(() => {
                // Ensure the spinner is reset regardless of success or failure
                this.spinner.reset();
              });
          } else {
            this.spinner.reset();
          }
          //move the unsubscribe out to make sure it is called in any navigationEnd event.
          navigationSubscription.unsubscribe(); // Unsubscribe from router events
        }
      });
    }).catch(() => {
      // Reset the spinner in case of navigation failure
      this.spinner.reset();
    }).finally(() => {
      // Reset spinner with finally after API call and navigation completes
      this.spinner.reset();
    });
  }

  private fetchVitals(): void {
    this.spinner.start();
    this.ehrpatientService
      .getObservationVitalSigns(this.ehrpatient.id)
      .subscribe({
        next: (data) => {
          if (data) {
            this.utility.data.vitalsigns = JSON.stringify(data);
            this.routeVitals(data);
          }
          this.spinner.end();
        },
        error: (error) => {
          this.vitalState = "error";
          this.spinner.reset();
          this.router.navigate(["./error"], { relativeTo: this.route });
          this.appLogger.logError(error, {
            component: "PatientDetailComponent",
            context: "getVitals",
            url: this.utility.data.url,
            crid: this.crid,
            startDate: this.utility.data.startDate,
            endDate: this.utility.data.endDate
          });
        }
      });
  }

  getVitals() {
    this.dialogRef.afterClosed().subscribe((result) => {
      if (result) this.fetchVitals();
      this.dialogRef = null;
    });
  }

  routeVitals(data) {
    this.vitalState = this.utility.bundleResources(JSON.stringify(data));
    const path = this.vitalState.total === 0 ? './info' : './vitalsigns';

    if (this.router.url === '/patientdetail/vitalsigns' && path === './vitalsigns') {
      // Navigate to '/patientdetail' and then to './vitalsigns'
      this.router.navigateByUrl('/patientdetail').then(() => {
        this.router.navigate([path], { relativeTo: this.route });
      });
    } else {
      // Navigate directly to the 'path'
      this.router.navigate([path], { relativeTo: this.route });
    }
  }

  /**
   * Refactored confirmation dialog code out and separated it from api calls for fetching data
   * @param resource
   */
  openConfirmationDialog(resource: string) {
    this.dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '490px',
      panelClass: "custom-dialog-container",
      disableClose: false
    });
    // No need to show checkbox during Observation calls
    this.dialogRef.componentInstance.showCheckbox = false;

    // date range is now in months - need to check 24 instead of 2
    if (this.dateRangeInMonths > 24 ) {
      this.dialogRef.componentInstance.confirmMessage = AppConfig.DATE_RANGE_COMMON_MESSAGE;
    }
    if (resource === 'vitals') {
      if (this.dateRangeInMonths <= 24) {
        this.dialogRef.componentInstance.confirmMessage = AppConfig.DATE_RANGE_MESSAGE_VITALS;
      }
      this.getVitals();
    }
    else if (resource === 'labs') {
      if (this.dateRangeInMonths <= 24) {
        this.dialogRef.componentInstance.confirmMessage = AppConfig.DATE_RANGE_MESSAGE_ALL_LABS;
      }
      this.getAllLabs();
    }
    else if (resource === 'priority') {
      if (this.dateRangeInMonths <= 24) {
        this.dialogRef.componentInstance.confirmMessage = AppConfig.DATE_RANGE_MESSAGE_PRIO_LABS;
      }
      this.getPriorityLabs();
    }
  }
}
